// Connection manager — dual-path routing (cloud vs LAN hub) + offline command queue
import hubDiscovery from './hubDiscovery'
import {
    enqueueCommand, getPendingCommands, markCommandSynced, markCommandFailed,
    clearSyncedCommands, cacheDevices, getCachedDevices, cacheHubs, getCachedHubs,
    saveMeta, getMeta,
} from './offlineDb'

const SYNC_INTERVAL = 10000
const CLOUD_TIMEOUT = 8000

class ConnectionManager {
    constructor() {
        this.cloudOnline = navigator.onLine
        this.socketConnected = false
        this.syncing = false
        this.syncTimer = null
        this.listeners = new Set()
        this._mode = 'cloud' // 'cloud' | 'lan' | 'offline'

        // Listen for browser online/offline events
        if (typeof window !== 'undefined') {
            window.addEventListener('online', () => this._onNetworkChange(true))
            window.addEventListener('offline', () => this._onNetworkChange(false))
        }
    }

    // Initialize — restore cached state, probe hubs, start sync loop
    async init() {
        this.cloudOnline = navigator.onLine
        await hubDiscovery.discoverFromCache()
        hubDiscovery.startHealthChecks()
        this._updateMode()
        this._startSyncLoop()
    }

    // Current connection mode
    get mode() { return this._mode }

    get status() {
        return {
            mode: this._mode,
            cloudOnline: this.cloudOnline,
            socketConnected: this.socketConnected,
            hasLocalHub: hubDiscovery.hasLocalHub(),
            localHubs: hubDiscovery.getOnlineHubs(),
        }
    }

    // Called by socket service when connection state changes
    setSocketConnected(connected) {
        this.socketConnected = connected
        this._updateMode()
    }

    // Test if cloud API is reachable
    async checkCloud() {
        try {
            const controller = new AbortController()
            const timeout = setTimeout(() => controller.abort(), CLOUD_TIMEOUT)
            const res = await fetch('/api/health', { signal: controller.signal })
            clearTimeout(timeout)
            this.cloudOnline = res.ok
        } catch {
            this.cloudOnline = false
        }
        this._updateMode()
        return this.cloudOnline
    }

    // ---- Dual-path API call ----
    // Tries cloud first, falls back to LAN hub, or queues offline
    async apiCall(config) {
        const { method = 'GET', url, data, hubId, hubCommand, offlineAction } = config

        // 1. Try cloud
        if (this.cloudOnline) {
            try {
                const res = await this._fetchCloud(method, url, data)
                return { source: 'cloud', data: res }
            } catch (err) {
                console.warn('[ConnMgr] Cloud call failed, trying LAN:', err.message)
                this.cloudOnline = false
                this._updateMode()
            }
        }

        // 2. Try LAN hub
        if (hubId && hubCommand && hubDiscovery.hasLocalHub()) {
            try {
                const res = await hubDiscovery.sendLocalCommand(hubId, hubCommand)
                return { source: 'lan', data: res }
            } catch (err) {
                console.warn('[ConnMgr] LAN command failed:', err.message)
            }
        }

        // 3. Queue offline if it's a write operation
        if (offlineAction && method !== 'GET') {
            await enqueueCommand({
                method, url, data, hubId, hubCommand,
                action: offlineAction,
            })
            return { source: 'queued', data: null }
        }

        // 4. For GET requests, try IndexedDB cache
        throw new Error('No connection available')
    }

    // Send a device command with full fallback chain
    async sendDeviceCommand(deviceMac, command, hubId) {
        return this.apiCall({
            method: 'POST',
            url: '/api/trigger',
            data: { mac: deviceMac, ...command },
            hubId,
            hubCommand: { mac: deviceMac, ...command },
            offlineAction: `device:${command.action || 'command'}`,
        })
    }

    // Cache device list when fetched from cloud
    async cacheDeviceList(devices) {
        await cacheDevices(devices)
    }

    // Get devices from cache when offline
    async getOfflineDevices() {
        return getCachedDevices()
    }

    // Cache hub list
    async cacheHubList(hubs) {
        await cacheHubs(hubs)
        // Also register hub IPs for LAN discovery
        for (const hub of hubs) {
            if (hub.ip || hub.localIP) {
                await hubDiscovery.registerHub(hub._id, hub.ip || hub.localIP, hub.webPort || 80)
            }
        }
    }

    async getOfflineHubs() {
        return getCachedHubs()
    }

    // ---- Sync queue ----

    async syncQueue() {
        if (this.syncing || !this.cloudOnline) return
        this.syncing = true

        try {
            const pending = await getPendingCommands()
            if (pending.length === 0) { this.syncing = false; return }

            console.log(`[ConnMgr] Syncing ${pending.length} queued commands...`)

            for (const cmd of pending) {
                try {
                    await this._fetchCloud(cmd.method, cmd.url, cmd.data)
                    await markCommandSynced(cmd.id)
                } catch (err) {
                    console.warn(`[ConnMgr] Sync failed for command ${cmd.id}:`, err.message)
                    await markCommandFailed(cmd.id, err.message)
                    if (err.message?.includes('401') || err.message?.includes('403')) {
                        // Auth failure — don't retry
                        continue
                    }
                    // Network failure — stop sync, will retry next cycle
                    break
                }
            }

            await clearSyncedCommands()
        } finally {
            this.syncing = false
        }
    }

    // ---- Internal helpers ----

    async _fetchCloud(method, url, data) {
        const token = localStorage.getItem('authToken')
        const opts = {
            method,
            headers: {
                'Content-Type': 'application/json',
                ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
            },
        }
        if (data && method !== 'GET') {
            opts.body = JSON.stringify(data)
        }
        const controller = new AbortController()
        const timeout = setTimeout(() => controller.abort(), CLOUD_TIMEOUT)
        opts.signal = controller.signal

        const res = await fetch(url, opts)
        clearTimeout(timeout)
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        const contentType = res.headers.get('content-type')
        if (contentType?.includes('application/json')) {
            return res.json()
        }
        return res.text()
    }

    _onNetworkChange(online) {
        this.cloudOnline = online
        this._updateMode()
        if (online) {
            // Try to sync queued commands when we come back online
            setTimeout(() => this.syncQueue(), 2000)
        }
    }

    _updateMode() {
        const prev = this._mode
        if (this.cloudOnline || this.socketConnected) {
            this._mode = 'cloud'
        } else if (hubDiscovery.hasLocalHub()) {
            this._mode = 'lan'
        } else {
            this._mode = 'offline'
        }
        if (prev !== this._mode) {
            this._notifyListeners()
        }
    }

    _startSyncLoop() {
        if (this.syncTimer) return
        this.syncTimer = setInterval(() => {
            if (this.cloudOnline) this.syncQueue()
        }, SYNC_INTERVAL)
    }

    // Listener pattern
    onChange(callback) {
        this.listeners.add(callback)
        return () => this.listeners.delete(callback)
    }

    _notifyListeners() {
        const state = this.status
        for (const cb of this.listeners) {
            try { cb(state) } catch {}
        }
    }

    destroy() {
        if (this.syncTimer) { clearInterval(this.syncTimer); this.syncTimer = null }
        hubDiscovery.stopHealthChecks()
    }
}

const connectionManager = new ConnectionManager()
export default connectionManager
